import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, X, Loader2 } from "lucide-react";
import { useLang } from "../lib/i18n";
import { PageShell } from "../components/PageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Box = ({ icon, title, sub, children }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="max-w-xl mx-auto text-center">
    <div className="w-16 h-16 mx-auto rounded-full border border-[#C9A66B] flex items-center justify-center text-[#8A6A3A]">{icon}</div>
    <h1 className="mt-8 font-serif-d font-light text-4xl sm:text-6xl text-[#14110F]" data-testid="payment-title">{title}</h1>
    <p className="mt-5 text-[#4f4841] font-light">{sub}</p>
    {children}
  </motion.div>
);

const BackBtn = ({ label }) => (
  <Link to="/" data-testid="payment-back-btn" className="mt-10 inline-flex rounded-full bg-[#14110F] text-[#F6F2EC] px-8 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#8A6A3A]">{label}</Link>
);

export function PaymentSuccess() {
  const { t } = useLang();
  const p = t.payment;
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });

  useEffect(() => {
    if (!sid) { setState({ phase: "failed" }); return undefined; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        if (data?.payment_status === "paid") { setState({ phase: "paid", data }); return; }
        if (data?.status === "expired" || data?.payment_status === "failed") { setState({ phase: "failed" }); return; }
      } catch { /* retry */ }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setState({ phase: "failed" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);

  return (
    <PageShell>
      {state.phase === "checking" && <Box icon={<Loader2 className="w-6 h-6 animate-spin" />} title={p.checking} sub="" />}
      {state.phase === "failed" && <Box icon={<X className="w-6 h-6" />} title={p.failTitle} sub={p.failSub}><BackBtn label={p.back} /></Box>}
      {state.phase === "paid" && (
        <Box icon={<Check className="w-6 h-6" />} title={p.successTitle} sub={p.successSub}>
          <div className="mt-10 rounded-2xl bg-[#14110F] text-[#F6F2EC] p-8 text-left" data-testid="payment-voucher">
            <p className="overline text-[0.62rem] text-[#C9A66B]">{p.code}</p>
            <p className="font-stencil text-3xl tracking-[0.2em] mt-2" data-testid="payment-voucher-code">{state.data?.code}</p>
            <p className="font-serif-d text-5xl mt-6">€{Math.round(state.data?.amount ?? 0)}</p>
            <p className="text-sm text-[#d9d0c4] mt-2">{state.data?.treatment || t.gift.any} · {t.gift.forLabel} {state.data?.recipient_name}</p>
          </div>
          <BackBtn label={p.back} />
        </Box>
      )}
    </PageShell>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  return (
    <PageShell>
      <Box icon={<X className="w-6 h-6" />} title={t.payment.cancelTitle} sub={t.payment.cancelSub}>
        <Link to="/#gift" data-testid="payment-retry-btn" className="mt-10 inline-flex rounded-full bg-[#14110F] text-[#F6F2EC] px-8 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#8A6A3A]">{t.payment.retry}</Link>
      </Box>
    </PageShell>
  );
}
